import type { BtmOption, SiteRagReport } from '../types/siteRag';

export type BtmDecisionState = 'btm-recommended' | 'grid-recommended' | 'hybrid-candidate' | 'decision-pending';

const KEYWORDS_BY_STATE: Record<BtmDecisionState, string[]> = {
  'btm-recommended': ['btm-only', 'behind-the-meter', 'onsite generation'],
  'grid-recommended': ['grid-only', 'utility-only', 'grid supplied'],
  'hybrid-candidate': [
    'hybrid', 'grid+btm', 'grid and btm',
    // Behind-the-meter is a meter arrangement: generation running in
    // parallel with grid supply (export or not) is a hybrid, not BTM-only.
    // "In parallel with" counts only when it names the grid or utility
    // ("air permitting runs in parallel with site design" is scheduling).
    'grid-parallel', 'in parallel with the grid', 'in parallel with grid',
    'in parallel with the utility', 'in parallel with utility', 'with grid backup',
  ],
  'decision-pending': [],
};

/**
 * "Non-export" reads as grid-parallel (hybrid) only when the summary does not
 * also say the plant is islanded: "BTM-only islanded gas generation,
 * non-export" is islanded BTM, not a hybrid.
 */
const NON_EXPORT_TOKEN = 'non-export';
const ISLANDED_WORDING = /\bislanded\b|\bisland mode\b/;

/** Clause-local negation just before a token: "rather than a hybrid", "not run in parallel with the grid". */
const NEGATED_LEAD = /\b(?:not|no|never|without|nor|rather than|instead of|cannot|can't|won't)(?:\s+\S+){0,2}\s*$/;
const CLAUSE_BREAK = /[.;:!?,()]|\s[—–]\s|\b(?:but|however|whereas)\b/g;
/** "Hybrid cooling", "hybrid wet/dry cooling towers": a heat-rejection hybrid, not a grid + BTM supply. */
const COOLING_AFTER_HYBRID = /^[\s-]*(?:[a-z/]+[\s-]+){0,2}(?:cool|heat[\s-]rejection)/;
/** "In parallel with grid interconnection studies / utility engagement": scheduling, not grid-parallel operation. */
const SCHEDULING_AFTER_PARALLEL = /^[\s-]*(?:interconnection|stud(?:y|ies)|application|queue|upgrade|permit|negotiation|discussion|engagement|outreach|filing|request|process)/;

function clauseBefore(summary: string, index: number): string {
  const prefix = summary.slice(0, index);
  let start = 0;
  for (const match of prefix.matchAll(CLAUSE_BREAK)) start = (match.index ?? 0) + match[0].length;
  return prefix.slice(start);
}

/** True when the summary contains the token at least once in an affirmative, power-supply sense. */
function affirms(summary: string, token: string): boolean {
  for (let index = summary.indexOf(token); index >= 0; index = summary.indexOf(token, index + 1)) {
    if (NEGATED_LEAD.test(clauseBefore(summary, index))) continue;
    const after = summary.slice(index + token.length, index + token.length + 48);
    if (token === 'hybrid' && COOLING_AFTER_HYBRID.test(after)) continue;
    if (token.startsWith('in parallel with') && SCHEDULING_AFTER_PARALLEL.test(after)) continue;
    return true;
  }
  return false;
}

function matchesState(state: Exclude<BtmDecisionState, 'decision-pending'>, summary: string): boolean {
  if (state !== 'hybrid-candidate') return KEYWORDS_BY_STATE[state].some(token => affirms(summary, token));
  return [...KEYWORDS_BY_STATE[state], ...[...summary.matchAll(/\bin parallel with (?:the )?(?:[a-z-]+\s+){0,3}(?:grid|utility)\b/g)].map(match => match[0])].some(token => affirms(summary, token))
    || (affirms(summary, NON_EXPORT_TOKEN) && !ISLANDED_WORDING.test(summary));
}

/**
 * Hybrid / grid-parallel wording is tested FIRST, because a hybrid summary
 * usually also says "behind-the-meter" or "onsite generation"; then BTM, then
 * grid-only. Hybrid tokens count only when affirmed (not negated, and
 * "hybrid" not describing cooling).
 */
const KEYWORD_PRECEDENCE: readonly Exclude<BtmDecisionState, 'decision-pending'>[] = [
  'hybrid-candidate',
  'btm-recommended',
  'grid-recommended',
];

export function getBtmDecisionSummary(report: SiteRagReport): string {
  return (
    report.btmDecisionSummary ??
    report.conclusion?.btmDecisionSummary ??
    report.btmViabilityAssessment ??
    report.gridVsBtmComparison?.[0]?.advantage ??
    'Decision pending'
  );
}

export function getBtmDecisionState(report: SiteRagReport): BtmDecisionState {
  const legacyBtmStrategy = (report as SiteRagReport & {
    btmStrategy?: 'btm-only' | 'grid-only' | 'hybrid';
  }).btmStrategy;
  if (legacyBtmStrategy === 'btm-only') return 'btm-recommended';
  if (legacyBtmStrategy === 'grid-only') return 'grid-recommended';
  if (legacyBtmStrategy === 'hybrid') return 'hybrid-candidate';

  const summary = getBtmDecisionSummary(report).toLowerCase();
  if (!summary || summary === 'decision pending') return 'decision-pending';
  return KEYWORD_PRECEDENCE.find(state => matchesState(state, summary)) ?? 'decision-pending';
}

const BTM_RISK_RANK: Record<string, number> = { Low: 0, Medium: 1, High: 2 };
const BTM_IMPACT_RANK: Record<string, number> = { Green: 0, Amber: 1, Red: 2 };

/** Best BTM option for cover/display: lowest risk, then lowest impact. */
export function pickLeadingBtmOption(options: BtmOption[]): BtmOption | null {
  if (options.length === 0) return null;
  return [...options].sort((a, b) => {
    const riskDiff =
      (BTM_RISK_RANK[a.riskLevel ?? ''] ?? 99) - (BTM_RISK_RANK[b.riskLevel ?? ''] ?? 99);
    if (riskDiff !== 0) return riskDiff;
    return (
      (BTM_IMPACT_RANK[a.impactLevel ?? ''] ?? 99) - (BTM_IMPACT_RANK[b.impactLevel ?? ''] ?? 99)
    );
  })[0];
}

export function getDecisionStateLabel(state: BtmDecisionState): string {
  switch (state) {
    case 'btm-recommended':
      return 'BTM recommended';
    case 'grid-recommended':
      return 'Grid-only recommended';
    case 'hybrid-candidate':
      return 'Hybrid candidate';
    default:
      return 'Decision pending';
  }
}

export function getTrustCue(report: SiteRagReport): {
  citedRefs: number;
  providedRefs: number;
  staleLabel: string;
  rationale: string;
} {
  const providedRefs = report.documentRefs?.filter(ref => ref.userIncluded).length ?? 0;
  const citedRefs = report.references?.length ?? 0;
  const ageMs = Date.now() - new Date(report.createdAt).getTime();
  const staleDays = Math.floor(ageMs / (1000 * 60 * 60 * 24));
  const staleLabel = staleDays > 45 ? `Stale (${staleDays}d)` : `Fresh (${staleDays}d)`;
  const rationale =
    report.conclusion?.topConstraints?.[0] ??
    report.conclusion?.topRisks?.[0] ??
    report.recommendations?.[0]?.title ??
    'No key constraint captured';
  return { citedRefs, providedRefs, staleLabel, rationale };
}
